import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { PlatformMessageTemplate } from "../../../cores/platformAutomations/services/api";
import { computeTimeline } from "../../../cores/platformAutomations/utils/draftDefinition.utils";
import { PlatformAutomationDraft } from "../../../cores/platformAutomations/types";

type Props = {
  draft: PlatformAutomationDraft;
  templates: PlatformMessageTemplate[];
  selectedStepId: string;
  onSelectStep: (stepId: string) => void;
  targetEmail: string;
  onTargetEmailChange: (email: string) => void;
  onSend: (stepId: string) => void;
  onRunFlow: () => void;
  sending?: boolean;
  runningFlow?: boolean;
};

export default function PlatformAutomationTestPanel({
  draft,
  templates,
  selectedStepId,
  onSelectStep,
  targetEmail,
  onTargetEmailChange,
  onSend,
  onRunFlow,
  sending = false,
  runningFlow = false,
}: Props) {
  const templateMap = useMemo(
    () =>
      templates.reduce<Record<number, { name: string; subject?: string }>>(
        (acc, t) => {
          acc[t.id] = { name: t.name, subject: t.draftDefinition?.subject };
          return acc;
        },
        {}
      ),
    [templates]
  );

  const actionableSteps = useMemo(
    () =>
      computeTimeline(draft.steps || [], templateMap).filter(
        (item) => item.type === "send_email" || item.type === "send_whatsapp"
      ),
    [draft.steps, templateMap]
  );

  if (!actionableSteps.length) {
    return (
      <div className="pa-empty-state">
        <FontAwesomeIcon icon={["fas", "envelope"]} />
        <p>
          <FormattedMessage id="app.platformAutomations.editor.testEmpty" />
        </p>
      </div>
    );
  }

  return (
    <div className="pa-test">
      <div className="pa-test__intro">
        <h3>
          <FormattedMessage id="app.platformAutomations.editor.testTitle" />
        </h3>
        <p>
          <FormattedMessage id="app.platformAutomations.editor.testHint" />
        </p>
        <p className="pa-test__whatsapp-note">
          Para probar pasos WhatsApp, el usuario destino debe tener teléfono
          cargado en su perfil.
        </p>
      </div>

      <label className="pa-field pa-test__email-field">
        <span>
          <FormattedMessage id="app.platformAutomations.editor.testTarget" />
        </span>
        <input
          type="email"
          value={targetEmail}
          onChange={(e) => onTargetEmailChange(e.target.value)}
          placeholder="usuario@ejemplo.com"
        />
      </label>

      <div className="pa-test__flow-card">
        <div>
          <strong>
            <FormattedMessage id="app.platformAutomations.editor.testFlowTitle" />
          </strong>
          <p>
            <FormattedMessage id="app.platformAutomations.editor.testFlowHint" />
          </p>
        </div>
        <button
          type="button"
          className="email-mkt__primary-btn"
          disabled={runningFlow || sending}
          onClick={onRunFlow}
        >
          {runningFlow ? (
            <FormattedMessage id="app.platformAutomations.editor.testFlowRunning" />
          ) : (
            <FormattedMessage id="app.platformAutomations.editor.testFlowRun" />
          )}
        </button>
      </div>

      <div className="pa-test__divider">
        <span>
          <FormattedMessage id="app.platformAutomations.editor.testPerStep" />
        </span>
      </div>

      <div className="pa-test__list">
        {actionableSteps.map((item) => {
          const selected = item.stepId === selectedStepId;
          return (
            <div
              key={item.stepId}
              className={`pa-test__item ${selected ? "is-selected" : ""}`}
            >
              <button
                type="button"
                className="pa-test__item-select"
                onClick={() => onSelectStep(item.stepId)}
              >
                <span className="pa-test__day">{item.offsetLabel}</span>
                <strong>{item.label}</strong>
                {item.detail && <span>{item.detail}</span>}
              </button>
              <button
                type="button"
                className={
                  selected ? "email-mkt__primary-btn" : "email-mkt__ghost-btn"
                }
                disabled={sending || runningFlow}
                onClick={() => onSend(item.stepId)}
              >
                {sending && selected ? (
                  <FormattedMessage id="app.platformAutomations.editor.testSending" />
                ) : (
                  <FormattedMessage id="app.platformAutomations.editor.testSend" />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
